import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import AdvancedArrayInput from '../AdvancedArrayInput';
import '../../styles/TreatmentPlanModal.css';
import CustomSelect from '../widgets/CustomSelect';
import { useAuth } from '../../hooks/useAuth';

const TreatmentPlanModal = ({
  admissionId,
  plan = null,
  isOpen,
  onClose,
  onSave,
}) => {
  const { user } = useAuth();
  
  // Initialize form data structure
  const emptyData = {
    doctor_name: '',
    start_date: new Date().toISOString().slice(0,16),
    end_date: '',
    notes: '',
    plan_items: [],
    is_active: true,
    admission_id: admissionId,
  };

  const [formData, setFormData] = useState(emptyData);
  const [initialData, setInitialData] = useState(emptyData);
  const [submitting, setSubmitting] = useState(false);

  // On open or plan change, populate form and snapshot initial
  useEffect(() => {
    if (!isOpen) return;
    let init = { ...emptyData };
    if (plan) {
      init = {
        doctor_name: plan.doctor_name || '',
        start_date: plan.start_date ? new Date(plan.start_date).toISOString().slice(0,16) : new Date().toISOString().slice(0,16),
        //end_date: plan.end_date ? new Date(plan.end_date).toISOString().slice(0,16) : '',
        notes: plan.notes || '',
        plan_items: Array.isArray(plan.plan_items) ? plan.plan_items : [],
        is_active: true,        // when editing, new plan will be active
        admission_id: admissionId,
      };
    }
    setFormData(init);
    setInitialData(init);
  }, [isOpen, plan, admissionId]);

  // Detect if form has changed
  const hasChanges = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(initialData);
  }, [formData, initialData]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const checkMedicationItems = (items) => {
    //check that if Type is Medication, then item_id is should be set. If not, return false
    return items.every(item => {
      if (item.Type === 'Medication') {
        return item.item_id && item.item_id.trim() !== '';
      }
      return true; // Non-medication items are always valid
    }
    );  

  };
  
  const handleSelectChange = (name, value) => {
    console.log('handleSelectChange', name, value);
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  const handleItemsChange = (name, items) => {
    console.log('handleItemsChange', name, items);

    if(checkMedicationItems(items) === false) {
      alert('For Medication types, you must select and item from our Pharmacy list.');
    }
    // Update form data with new items
    setFormData(prev => ({ ...prev, [name]: items }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasChanges) return;

    if (!formData.doctor_name) {
      alert('Doctor name is required');
      return;
    }
    if (!formData.start_date) {
      alert('Start date is required');
      return;
    }
    if (formData.plan_items.length === 0) {
      alert('At least one treatment item is required');
      return;
    }
    // Validate medication items
    if (formData.plan_items.some(item => item.Type === '' || !item.Type)) {
      alert('Please select a type for each treatment item.');
      return;
    }
    // validate that for medication types, Route, Dosage, and Frequency are set
    if (formData.plan_items.some(item => item.Type === 'Medication' && (!item.route || !item.Dosage || !item.frequency))) {
      alert('For Medication types, Route, Dosage, and Frequency must be set.');
      return;
    }

    if (!checkMedicationItems(formData.plan_items)) {
      alert('For Medication types, you must select an item from our Pharmacy list.');
      return;
    }
    // set end_date to today at 10am
    const today = new Date();
    const endDate = new Date(today);
    endDate.setHours(10, 0, 0, 0); // Set to 10:00 AM today
    formData.end_date = endDate.toISOString().slice(0,16); // Format to match input type="datetime-local"
    
    // Add updatedBy field
    const submitData = {
      ...formData,
      updatedBy: user?.portal_id
    };
    
    setSubmitting(true);
    try {
      // Always create a new plan to preserve history
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/admissions/treatment-plans`,
        submitData
      );
      onSave(response.data);
      onClose();
    } catch (err) {
      console.error('Error creating treatment plan:', err);
      alert(err.response?.data?.message || 'Failed to save plan');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="treatment-modal-content">
        <h2>{plan ? 'Edit Treatment Plan' : 'New Treatment Plan'}</h2>
        <form onSubmit={handleSubmit} className="tp-modal-form">
          <div className="form-group">
            <label>Doctor Name*</label>
            <CustomSelect
              name="doctor_name"
              value={formData.doctor_name}
              endpoint="/hospital-portal/api/employees/doctors"
              onChange={handleSelectChange}
              required
              valueField="doctor_name"
              labelField="doctor_name"
              placeholder="Select a doctor"
              searchable
              error={formData.doctor_name ? '' : 'Doctor name is required'}
                />
          </div>
          <div className="form-group-inline">
            <div>
              <label>Start Date*</label>
              <input
                name="start_date"
                type="datetime-local"
                value={formData.start_date}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label></label>
              <input
                name="end_date"
                type="datetime-local"
                value={formData.end_date}
                onChange={handleChange}
                hidden={true} // Hide end date for now
              />
            </div>
          </div>

          <div className="form-group">
            <label>Treatment Items*</label>
            <AdvancedArrayInput
              name="plan_items"
              value={formData.plan_items}
              endpoint="/hospital-portal/api/items/search/by-name"
              onChange={handleItemsChange}
              additionalFields={[
                { name: 'Type', label: 'Type', type: 'dropdown', options: [
                  { value: 'Medication', label: 'Medication' },
                  { value: 'Lab', label: 'Lab' },
                  { value: 'Other', label: 'Other' },
                ], width: '10%' },
                { name: 'route', label: 'Route', type: 'dropdown', options: [
                  { value: 'IV', label: 'IV' },
                  { value: 'IM', label: 'IM' },
                  { value: 'PO', label: 'PO' },
                  { value: 'PR', label: 'PR' },
                  { value: 'S/C', label: 'S/C' },
                  { value: 'PV', label: 'PV' },
                ], width: '10%' },
                { name: 'Dosage', label: 'Dosage', type: 'text', width: '10%' },
                { name: 'frequency', label: 'Frequency', type: 'dropdown', options: [
                  { value: 'OD', label: 'OD' },
                  { value: 'BD', label: 'BD' },
                  { value: 'TDS', label: 'TDS' },
                  { value: 'QID', label: 'QID' },
                  { value: 'One-Time', label: 'One-Time' },
                  { value: 'PRN', label: 'PRN' },
                  { value: 'Stat', label: 'Stat' },
                ], width: '5%' },
              ]}
              required
              showItemNotFoundWarning={false}
            />
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" disabled={submitting || !hasChanges}>
              {submitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TreatmentPlanModal;
